import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import Box from '@mui/material/Box';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import ListItemWithIcon from './ListItemWithIcon';

function NestedMenu({
  options,
  numOfLevels,
  handleSelectedItem,
  disabledItems = [],
  singleSelectionMode = false,
  onAction,
  onDelete,
}) {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState(Array(numOfLevels).fill(null));

  const buildMenuLevels = (list, selected) => {
    if (isEmpty(list)) return [];
    const result = [];
    let currentList = list;
    for (let i = 0; i < numOfLevels; i++) {
      result.push(currentList);
      if (currentList.length === 0) break;

      const selectedItem = currentList.find((item) => item.key === selected[i] || item.value === selected[i]) || currentList[0];
      if (selectedItem?.children?.length) {
        currentList = selectedItem.children;
      }
    }
    return result;
  };

  const updateSelectedItem = (value, level) => {
    const newSelected = selected.slice(0, level + 1);
    newSelected[level] = value.key || value.value;

    let currentList = value.children || [];
    for (let i = level + 1; i < numOfLevels; i++) {
      newSelected[i] = null;
      if (i < numOfLevels - 1 && currentList.length) {
        newSelected[i] = currentList[0].key || currentList[0].value;
        currentList = currentList[0].children || [];
      }
    }
    return newSelected;
  };

  const handleChange = (value, level) => {
    const newSelected = updateSelectedItem(value, level);
    setData(buildMenuLevels(options, newSelected));
    setSelected(newSelected);

    // Notify parent when a final item is selected (no children)
    if (!value.children?.length) {
      handleSelectedItem(value, level);
    }
  };

  const initializeMenu = () => {
    if (isEmpty(options)) return;
    const newSelected = [...selected];
    let currentList = options;
    for (let i = 0; i < numOfLevels - 1; i++) {
      if (currentList.length === 0) {
        newSelected.fill(null, i);
        break;
      }
      if (newSelected[i] == null) {
        newSelected[i] = currentList[0].key || currentList[0].value;
      }
      currentList = currentList.find((item) => item.key === newSelected[i] || item.value === newSelected[i])?.children || [];
    }
    setData(buildMenuLevels(options, newSelected));
    setSelected(newSelected);
  };

  useEffect(() => {
    initializeMenu();
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container className="p-0">
      <Box sx={{ display: 'flex' }}>
        {data.map((list, index) => (
          <ListItemWithIcon
            key={index} // eslint-disable-line react/no-array-index-key
            list={list}
            level={index}
            onChange={handleChange}
            onAction={onAction}
            onDelete={onDelete}
            selectedIndex={list.findIndex((item) => item.key === selected[index] || item.value === selected[index])}
            disabledItems={disabledItems}
            singleSelectionMode={singleSelectionMode}
            isNotShowIcon={index === numOfLevels - 1}
          />
        ))}
      </Box>
    </Container>
  );
}

NestedMenu.propTypes = {
  options: PropTypes.array.isRequired,
  numOfLevels: PropTypes.number.isRequired,
  handleSelectedItem: PropTypes.func.isRequired,
  disabledItems: PropTypes.array,
  singleSelectionMode: PropTypes.bool,
  onAction: PropTypes.func,
  onDelete: PropTypes.func,
};

NestedMenu.defaultProps = {
  disabledItems: [],
  singleSelectionMode: false,
};

export default NestedMenu;
